/**
 * Document Helper Utilities
 * Provides helper functions for document viewing, downloading, and visibility display
 */

import React from 'react';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import { getApiFetchUrl } from '../../config/apiConfig';
import { log } from '../errors/logger';
import { DocumentVisibility } from '../../api';
import { TFunction } from 'i18next';

/**
 * Extract clean document ID from URL or ID string
 * @param documentId - Document ID or URL path
 * @returns Clean document ID
 */
function extractDocumentId(documentId: string): string {
  if (documentId.startsWith('/document/download/')) {
    return documentId.replace('/document/download/', '');
  }
  if (documentId.startsWith('/document/view/')) {
    return documentId.replace('/document/view/', '');
  }
  return documentId;
}

function buildApiFileUrl(path: string): string {
  return `${getApiFetchUrl()}${path}`;
}

/**
 * Download original document file
 * Uses centralized API client for proper authentication and error handling
 * @param documentId - Document ID or download URL
 */
export async function downloadOriginalDocument(documentId: string): Promise<void> {
  if (!documentId) {
    log.error('No document ID provided');
    return;
  }

  const cleanDocumentId = extractDocumentId(documentId);
  const downloadUrl = buildApiFileUrl(
    `/documents/document/download/${encodeURIComponent(cleanDocumentId)}`
  );

  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.rel = 'noopener noreferrer';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  log.info('Document download requested', { documentId: cleanDocumentId });
}

/**
 * View original document in new window
 * Uses centralized API client for proper authentication and error handling
 * @param documentId - Document ID or view URL
 */
export async function viewOriginalDocument(documentId: string): Promise<void> {
  if (!documentId) {
    log.error('No document ID provided');
    return;
  }

  const cleanDocumentId = extractDocumentId(documentId);
  const viewUrl = buildApiFileUrl(
    `/documents/document/view/${encodeURIComponent(cleanDocumentId)}`
  );

  // window.open(..., 'noopener') always returns null on success per the spec,
  // so a returned-null check would be a false positive. Use an anchor click instead.
  const anchor = document.createElement('a');
  anchor.href = viewUrl;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  log.info('Document opened for viewing', { documentId: cleanDocumentId });
}

// ==================== Visibility Helpers ====================

/**
 * Get icon component for document visibility level
 *
 * @param visibility - The document visibility setting
 * @returns React icon element with appropriate color
 *
 * @example
 * ```tsx
 * const icon = getDocumentVisibilityIcon(DocumentVisibility.Public);
 * // Returns <PublicIcon fontSize="small" color="success" />
 * ```
 */
export const getDocumentVisibilityIcon = (
  visibility: DocumentVisibility
): React.ReactElement => {
  switch (visibility) {
    case DocumentVisibility.Public:
      return React.createElement(PublicIcon, { fontSize: 'small', color: 'success' });
    case DocumentVisibility.Organization:
      return React.createElement(BusinessIcon, { fontSize: 'small', color: 'primary' });
    case DocumentVisibility.Group:
      return React.createElement(GroupIcon, { fontSize: 'small', color: 'info' });
    case DocumentVisibility.Private:
      return React.createElement(LockIcon, { fontSize: 'small', color: 'action' });
    default:
      return React.createElement(BusinessIcon, { fontSize: 'small', color: 'primary' });
  }
};

/**
 * Get human-readable text for document visibility
 *
 * @param doc - Document with visibility and shared_with_groups properties
 * @param t - i18next translation function
 * @returns Localized visibility description
 *
 * @example
 * ```tsx
 * const text = getDocumentVisibilityText(
 *   { visibility: DocumentVisibility.Groups, shared_with_groups: ['team1', 'team2'] },
 *   t
 * );
 * // Returns "Shared with 2 groups" (localized)
 * ```
 */
export const getDocumentVisibilityText = (
  doc: {
    visibility?: DocumentVisibility;
    shared_with_groups?: string[];
  },
  t: TFunction
): string => {
  const visibility = doc.visibility || DocumentVisibility.Organization;

  switch (visibility) {
    case DocumentVisibility.Public:
      return t('documents.visibility.public');
    case DocumentVisibility.Organization:
      return t('documents.visibility.organization');
    case DocumentVisibility.Group:
      return doc.shared_with_groups && doc.shared_with_groups.length > 0
        ? t('documents.visibility.groups_count', { count: doc.shared_with_groups.length })
        : t('documents.visibility.group');
    case DocumentVisibility.Private:
      return t('documents.visibility.private');
    default:
      return t('documents.visibility.organization');
  }
};
